import { useRef, useState } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";

/**
 * Hero video — auto-plays muted on mount with an unmute button overlaid.
 * Optional header banner appears above the video.
 *
 * Props:
 *   src         - video URL
 *   poster      - optional poster image
 *   header      - optional small heading shown above video ("Watch Video then…")
 *   className   - extra wrapper classes
 */
export default function HeroVideo({ src, poster, header, className = "", testid = "hero-video" }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    // If user un-mutes, try to play (in case autoplay paused on some browsers)
    if (!v.muted) {
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div className={`relative w-full max-w-3xl mx-auto ${className}`} data-testid={testid}>
      {header && (
        <div className="text-center mb-3">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#7C3AED]/20 to-[#EC4899]/20 border border-[#7C3AED]/40 text-[#A78BFA] text-xs md:text-sm font-semibold tracking-wide">
            <Play className="w-3.5 h-3.5 fill-current" /> {header}
          </span>
        </div>
      )}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-[#7C3AED]/20 bg-black aspect-video">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover cursor-pointer"
          onClick={togglePlay}
          data-testid={`${testid}-el`}
        />
        {/* Unmute control — bottom-right */}
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur border border-white/20 text-white text-xs font-medium transition-colors"
          data-testid={`${testid}-mute-btn`}
        >
          {muted ? (
            <>
              <VolumeX className="w-4 h-4" />
              <span className="hidden sm:inline">Tap to Unmute</span>
            </>
          ) : (
            <>
              <Volume2 className="w-4 h-4" />
              <span className="hidden sm:inline">Mute</span>
            </>
          )}
        </button>
        {/* Subtle pulse hint while muted */}
        {muted && (
          <div className="pointer-events-none absolute bottom-3 right-3 z-0 w-12 h-12 rounded-full bg-[#7C3AED]/30 blur-xl animate-pulse" />
        )}
      </div>
    </div>
  );
}
